import React, { useCallback, useEffect } from 'react';
import { Platform, ScrollView } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import { getIosModalPresentationConfig, useResponsive } from '@sg/garnish';
import {
  MenuDietaryPreferences,
  type AllergenOrDietaryRestriction,
  useDietaryPreferencesContent,
  getIconForAllergen,
  DietaryPreferencesContent,
} from '@sg/shared-fe/features/menu';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import { useDietaryPreferencesMachine } from '../../../../context/DietaryPreferences';
import { Modal } from '../../../Modal';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentDietaryPreferencesModal = (
  props: MenuContentDietaryPreferencesModalProps,
) => {
  const { isVisible, onClose } = props;

  const { match } = useResponsive();

  // ─── Content ───────────────────────────────────────────────────────────────

  const content = useContentfulContentTypeEntry<DietaryPreferencesContent>({
    contentType: 'dietaryPreferences',
  });

  const { title, subtitle, disclaimer, confirmCta, icons } = useDietaryPreferencesContent(
    content?.data?.fields,
  );

  // ─── Machine ───────────────────────────────────────────────────────────────

  const {
    isAllergensEnabled,
    sortedRestrictionsNames,
    pendingRestrictions,
    isUpdatingRemoteRestrictions,
    isFetchingCustomerPreferences,
    hasUnsavedPreferences,
    toggleRestriction,
    cancelChanges,
    confirmChanges,
    start,
    isIdle,
  } = useDietaryPreferencesMachine({ referrer: 'Menu' });

  // start machine when modal opens (or re-start if machine returns to idle while visible)
  // The machine auto-fetches customer preferences when START is sent.
  useEffect(() => {
    if (isVisible && isIdle) {
      start();
    }
  }, [isVisible, isIdle, start]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const resetAndClose = useCallback(() => {
    cancelChanges();
    onClose();
  }, [onClose, cancelChanges]);

  const confirmAndClose = useCallback(() => {
    confirmChanges();
    onClose();
  }, [confirmChanges, onClose]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size={match(['full', 'medium'])}
      fitHeight
      onRequestClose={resetAndClose}
      {...getIosModalPresentationConfig()}
    >
      <ScrollView>
        <SafeAreaView edges={SAFE_AREA_EDGES}>
          <MenuDietaryPreferences.Container>
            <MenuDietaryPreferences.ModalHeader>
              <MenuDietaryPreferences.ModalCloseButton onPress={resetAndClose} />
            </MenuDietaryPreferences.ModalHeader>

            <MenuDietaryPreferences.Heading>{title}</MenuDietaryPreferences.Heading>

            <MenuDietaryPreferences.Description>{subtitle}</MenuDietaryPreferences.Description>

            {isAllergensEnabled ? (
              <MenuDietaryPreferences.ButtonsContainerV2>
                {sortedRestrictionsNames.map((restriction) => (
                  <MenuDietaryPreferences.ButtonV2<AllergenOrDietaryRestriction>
                    icon={getIconForAllergen(restriction, icons)}
                    key={restriction}
                    restriction={restriction}
                    isLoading={isFetchingCustomerPreferences}
                    isChecked={pendingRestrictions[restriction]}
                    isDisabled={isUpdatingRemoteRestrictions}
                    toggleRestriction={toggleRestriction}
                  />
                ))}
              </MenuDietaryPreferences.ButtonsContainerV2>
            ) : (
              <MenuDietaryPreferences.ButtonsContainer>
                {sortedRestrictionsNames.map((restriction) => (
                  <MenuDietaryPreferences.Button<AllergenOrDietaryRestriction>
                    key={restriction}
                    restriction={restriction}
                    isLoading={isFetchingCustomerPreferences}
                    isChecked={pendingRestrictions[restriction]}
                    isDisabled={isUpdatingRemoteRestrictions}
                    toggleRestriction={toggleRestriction}
                  />
                ))}
              </MenuDietaryPreferences.ButtonsContainer>
            )}

            <MenuDietaryPreferences.SubmitButton
              label={confirmCta}
              isLoading={isUpdatingRemoteRestrictions}
              isDisabled={!hasUnsavedPreferences}
              onPress={confirmAndClose}
            />

            <MenuDietaryPreferences.Notices>{disclaimer}</MenuDietaryPreferences.Notices>
          </MenuDietaryPreferences.Container>
        </SafeAreaView>
      </ScrollView>
    </Modal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = Platform.select({
  android: ['top'],
  default: [],
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuContentDietaryPreferencesModalProps = {
  isVisible: boolean;
  onClose: () => void;
};
